import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Ban, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { format } from "date-fns";

const BlockedUsersPage = () => {
  const navigate = useNavigate();
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unblocking, setUnblocking] = useState(null);

  useEffect(() => {
    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = async () => {
    try {
      const response = await api.getBlockedUsers();
      setBlockedUsers(response.data);
    } catch (error) {
      toast.error("Failed to load blocked users");
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to unblock ${userName}?`)) return;
    
    setUnblocking(userId);
    try {
      await api.unblockUser(userId);
      setBlockedUsers(prev => prev.filter(u => u.id !== userId));
      toast.success(`${userName} has been unblocked`);
    } catch (error) {
      toast.error("Failed to unblock user");
    } finally {
      setUnblocking(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="blocked-users-page">
      {/* Header */}
      <div className="bg-white shadow-sm px-4 py-4 sticky top-0 z-10">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate("/settings")} className="p-1 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-6 h-6 text-[#1C3A7A]" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-[#1C3A7A]">Blocked Users</h1>
            <p className="text-sm text-gray-500">{blockedUsers.length} blocked</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#FAB710] border-t-transparent" />
          </div>
        ) : blockedUsers.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Ban className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-1">No blocked users</h3>
            <p className="text-sm text-gray-500">
              Users you block won't be able to see your profile or message you
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {blockedUsers.map((user) => (
              <div
                key={user.id}
                className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-sm"
                data-testid={`blocked-user-${user.id}`}
              >
                <img
                  src={user.photos?.[0] || "/placeholder-user.png"}
                  alt={user.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1">
                  <p className="font-semibold text-gray-900">{user.name}</p>
                  {user.blocked_at && (
                    <p className="text-xs text-gray-500">
                      Blocked {format(new Date(user.blocked_at), "MMM d, yyyy")}
                    </p>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleUnblock(user.id, user.name)}
                  disabled={unblocking === user.id}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid={`unblock-${user.id}`}
                >
                  {unblocking === user.id ? "..." : "Unblock"}
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 rounded-xl p-4">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
            <UserX className="w-4 h-4" />
            About Blocking
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Blocked users can't see your profile</li>
            <li>• They can't send you messages</li>
            <li>• Existing matches are automatically removed</li>
            <li>• They won't know they've been blocked</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BlockedUsersPage;
